/** In-product excerpts from the matching `zworks/docs/zot/commands/*.md` guides. */
export interface ZotCommandGuideContent {
  reads: string;
  canPropose: string[];
  never: string[];
  examples: string[];
}

export const zotCommandGuideContent: Record<string, ZotCommandGuideContent> = {
  "/room": {
    reads:
      "Project record, repositories, work items, usage history, and related Marketplace packs.",
    canPropose: [
      "Start a room after showing its estimate and asking for confirmation",
      "Create work items from the handoff",
      "Draft a workflow in Studio",
    ],
    never: [
      "Invent metrics or customers; unsupported numbers are marked ‘to be set’",
      "Present the simulated team as real people",
    ],
    examples: [
      "/room let customers retry a failed payment from the receipt page",
      "/room split the nightly export into per-region jobs\nWhat should stay out of scope for the first version?",
      "/room add single sign-on for the support team",
    ],
  },
  "/studio": {
    reads:
      "Workflow lists and versions, validation results, installed skills and tools, and usage by workflow.",
    canPropose: [
      "Create, edit, duplicate, or delete a workflow draft",
      "Bind or unbind a capability",
      "Install or upload a skill or OpenAPI spec for scanning",
      "Add or remove a tool",
      "Request publishing; an approver still publishes",
    ],
    never: [
      "Publish without an approver",
      "Install silently or widen capabilities without showing a diff",
    ],
    examples: [
      "/studio which workflows use the refund skill, and how often",
      "/studio duplicate the nightly-export workflow so I can edit the copy",
      "/studio bind the pdf-export tool to the invoice workflow",
    ],
  },
  "/run": {
    reads: "Run lists and filters, status, redacted logs, cost, evidence, and failure reasons.",
    canPropose: [
      "Start a task or run",
      "Retry a failed run or re-run from a step",
      "Cancel a run",
      "Raise a cap",
    ],
    never: [
      "Exceed a cap",
      "Show secrets; credentials appear as references only",
      "Hide unknown spend",
    ],
    examples: [
      "/run why did run_8f21 fail, and fix it",
      "/run retry the failed refund runs from last night",
      "/run cancel run_91ac and tell me what it cost so far",
    ],
  },
  "/work": {
    reads: "Work item lists and search results, owners, and links to runs and workflows.",
    canPropose: [
      "Create a work item from a handoff document",
      "Split an item into smaller items",
      "Update status, owner, and links",
    ],
    never: ["Delete without confirmation"],
    examples: [
      "/work what is left in the onboarding milestone",
      "/work file the refund-retry handoff as work items",
      "/work split ZW-118 into backend and frontend items, assign the backend one to Mai",
    ],
  },
  "/approvals": {
    reads: "Pending approval list, digest, revision, diff since it was opened, and risk notes.",
    canPropose: [
      "Prepare a recommendation marked as advice, with the risks and the current digest",
    ],
    never: ["Approve or reject any approval, including its own proposals"],
    examples: [
      "/approvals what is waiting for me right now",
      "/approvals summarise the risks on the refund-limit increase",
      "/approvals what changed in the export-workflow publish request since yesterday",
    ],
  },
  "/usage": {
    reads: "Spend, forecasts, ledger entries, cap status, and cost drivers.",
    canPropose: [
      "Change a cap or monthly limit (owner role required)",
      "Open a checkout link for a top-up",
    ],
    never: [
      "Charge without confirmation",
      "Hide unknown spend; uncategorised cost is shown as unknown",
    ],
    examples: [
      "/usage how much did the refund workflow cost this week",
      "/usage why is the forecast higher than last month",
      "/usage raise the nightly-export cap to 40 credits",
    ],
  },
  "/automation": {
    reads: "Automation lists, last deliveries, and failure history.",
    canPropose: [
      "Create a schedule or trigger",
      "Pause, resume, or edit an existing schedule or trigger",
    ],
    never: ["Run production deploys on a schedule without a gate; the approval path still applies"],
    examples: [
      "/automation which schedules failed last night",
      "/automation pause the weekly digest until Monday",
      "/automation run the invoice export every weekday at 06:00",
    ],
  },
  "/integrations": {
    reads: "Connection health, granted scopes, expiry, and recent activity.",
    canPropose: [
      "Request a scope for a connection",
      "Reconnect or rotate a connection",
      "Disconnect a connection (owner role required)",
    ],
    never: ["Reveal a secret in chat or accept one pasted into chat; credentials stay references"],
    examples: [
      "/integrations is the GitHub connection healthy",
      "/integrations reconnect the warehouse database",
      "/integrations which scopes does the deploy key still need",
    ],
  },
  "/market": {
    reads: "Pack search, version comparison, permissions, and evaluation results.",
    canPropose: ["Request installation, an update, or removal of a pack"],
    never: ["Install without the approval policy", "Publish a pack from chat"],
    examples: [
      "/market find a reviewed pdf-export pack",
      "/market compare versions 2.3 and 2.4 of the csv-import pack",
      "/market request the slack-notify pack for this project",
    ],
  },
  "/evidence": {
    reads: "Evidence packs, signatures, and audit search results.",
    canPropose: ["Build or export an evidence pack for a run, approval, or period"],
    never: ["Alter audit entries"],
    examples: [
      "/evidence build a pack for run_8f21 for the auditors",
      "/evidence who approved the refund-limit increase, and when",
      "/evidence export last month's deploy approvals as PDF",
    ],
  },
  "/project": {
    reads: "Roles, project defaults, and retention controls.",
    canPropose: [
      "Invite a member",
      "Change a role, a default, or retention",
      "Start a project data export (owner role required)",
    ],
    never: [
      "Change security settings without the owner's confirmation",
      "Bypass owner-only rules for other roles",
    ],
    examples: [
      "/project who can approve deploys in this project",
      "/project invite lan@example.com as an operator",
      "/project keep chat history for 30 days",
    ],
  },
  "/help": {
    reads: "Static command help and examples; no project-specific data.",
    canPropose: [],
    never: ["Change state"],
    examples: ["/help", "/help room", "/help how do I retry a failed run"],
  },
};
